import { useState } from "react";
import { Alert, Box } from "@mui/material";
import { DotLegalButton, DotLegalHeader, DotLegalLink, DotLegalPaper, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import SetMonthlySpendingLimitDialog from "./setMonthlySpendingLimitDialog/SetMonthlySpendingLimitDialog";
import WrappedGift from "../wrapped-gift.svg?react";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";

export interface IMonthlySpendingLimitBox {
    welcomeCreditEur: number;
    monthlySpendingLimitEur: number | null;
    currentMonthlySpendingEur: number;
    isLoading: boolean;
    onLimitSaved: () => void;
    hasWelcomeCreditBeenExhausted?: boolean;
}

function MonthlySpendingLimitBox(props: IMonthlySpendingLimitBox) {
    const { translateString } = useTranslation();
    const { addonsFeatures } = usePlanContext();
    const [showLimitDialog, setShowLimitDialog] = useState(false);
    const { language } = useUserContext();

    const handleLimitSaved = () => {
        setShowLimitDialog(false);
        props.onLimitSaved();
    };

    return (
        <DotLegalPaper fullHeight fillColumn>
            <Box sx={{ marginRight: 6, marginLeft: 6, marginTop: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <DotLegalHeader headerStyle="medium">
                        <Box sx={{ textTransform: "uppercase" }}>{translateString("monthlySpendingLimit")}</Box>
                    </DotLegalHeader>
                    <DotLegalButton buttonType="primary" disabled={!addonsFeatures.aiFeatures} onClick={() => setShowLimitDialog(true)}>
                        {translateString("setLimit")}
                    </DotLegalButton>
                </Box>
                <DotLegalHeader headerStyle="small" marginTop={2} fontWeight={400}>
                    {translateString("monthlySpendingLimitDescription")}{" "}
                    <DotLegalLink
                        linkColor="primary"
                        to={
                            language === "da"
                                ? "https://www.dotlegal.com/da/produkt/ai-features#ai-prismodel"
                                : "https://www.dotlegal.com/en/product/ai-features#ai-prices"
                        }
                        bold
                        underline
                        openExternalLinkInSameTab={false}
                    >
                        {translateString("learnMore")}
                    </DotLegalLink>
                    .
                </DotLegalHeader>

                <DotLegalTextField
                    label=""
                    value={props.monthlySpendingLimitEur?.toString() ?? ""}
                    disabled
                    debounce={false}
                    onlyNumber="decimal"
                    isLoading={props.isLoading}
                    endAdorment={<Box sx={{ color: "text.secondary" }}>EUR</Box>}
                />

                {!props.hasWelcomeCreditBeenExhausted && !props.isLoading && (
                    <Alert sx={{ marginTop: 2, backgroundColor: "#E6FAEd", color: "#36847D" }} icon={false}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                            <WrappedGift width={20} height={20} />
                            {addonsFeatures.aiFeatures
                                ? translateString("aiWelcomeOfferActivatedAlert", { amount: props.welcomeCreditEur })
                                : translateString("aiWelcomeOfferAlert", { amount: props.welcomeCreditEur })}
                        </Box>
                    </Alert>
                )}

                {showLimitDialog && (
                    <SetMonthlySpendingLimitDialog
                        currentLimit={props.monthlySpendingLimitEur ?? undefined}
                        currentMonthlySpendingEur={props.currentMonthlySpendingEur}
                        onDialogClose={() => setShowLimitDialog(false)}
                        onLimitSaved={handleLimitSaved}
                    />
                )}
            </Box>
        </DotLegalPaper>
    );
}

export default MonthlySpendingLimitBox;
